import type { ReactNode } from 'react';

// Geometric stroke icons, carried verbatim from _prototype/components.jsx.
// One shared primitive; bookings uses a subset, the full set stays for reuse.
const ICONS: Record<string, ReactNode> = {
  // WhatsApp's own mark (Freddi, 2026-10-11: "ensure whatsapp icon used") — a filled glyph in a stroked set, so it carries its own fill.
  whatsapp: (
    <path fill="currentColor" stroke="none" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  check: <path d="M20 6 9 17l-5-5" />,
  // Copy / duplicate — two overlapping sheets (Lucide copy).
  copy: (
    <>
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </>
  ),
  close: <path d="M18 6 6 18M6 6l12 12" />,
  // A document — a PDF proof's thumbnail (Lucide file-text).
  'file-text': (
    <>
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4M10 9H8M16 13H8M16 17H8" />
    </>
  ),
  'log-out': (
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5" />
      <path d="M21 12H9" />
    </>
  ),
  // Exact mirror of log-out — arrow going INTO the door. Used for "Arrived".
  'log-in': (
    <>
      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
      <path d="m10 17 5-5-5-5" />
      <path d="M15 12H3" />
    </>
  ),
  // Eye — a viewing. The only genuinely absent asset this feature needed: the
  // set had no eye, door or tour glyph, and every candidate already meant
  // something else on this desk.
  eye: (
    <>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  // Hourglass — "Unconfirmed" (awaiting deposit / pending).
  hourglass: (
    <>
      <path d="M5 22h14" />
      <path d="M5 2h14" />
      <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22" />
      <path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2" />
    </>
  ),
  // The request card's three state glyphs (docs/design/accept-or-decline.html):
  // a request waits on her (clipboard), accepted waits on the deposit (pin),
  // confirmed is booked (calendar with a tick).
  'clipboard-check': (
    <>
      <rect width="8" height="4" x="8" y="2" rx="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <path d="m9 14 2 2 4-4" />
    </>
  ),
  pin: (
    <>
      <path d="M12 17v5" />
      <path d="M9 10.8a2 2 0 0 1-1.1 1.8l-1.8.9A2 2 0 0 0 5 15.3V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.7a2 2 0 0 0-1.1-1.8l-1.8-.9A2 2 0 0 1 15 10.8V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1Z" />
    </>
  ),
  'calendar-check': (
    <>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
      <path d="m9 16 2 2 4-4" />
    </>
  ),
  // Cross inside a circle — "Declined" (prototype 16r's crossed-circle tile;
  // distinct from the bare close that Cancelled wears).
  'circle-x': (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="m15 9-6 6" />
      <path d="m9 9 6 6" />
    </>
  ),
  // Check inside a circle — "Confirmed" (sealed, distinct from the bare check).
  'circle-check': (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  // Bulleted list — the "All" filter (every booking, no status filter).
  list: (
    <>
      <path d="M8 6h13" />
      <path d="M8 12h13" />
      <path d="M8 18h13" />
      <path d="M3 6h.01" />
      <path d="M3 12h.01" />
      <path d="M3 18h.01" />
    </>
  ),
  menu: (
    <>
      <path d="M3 6h18" />
      <path d="M3 12h18" />
      <path d="M3 18h18" />
    </>
  ),
  left: <path d="m15 18-6-6 6-6" />,
  right: <path d="m9 18 6-6-6-6" />,
  'chevron-down': <path d="m6 9 6 6 6-6" />,
  // Notification bell — placeholder chrome for AC6 (no live content this build).
  // Info — the staff view's third tab (shift-alerts-info-tab).
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8h.01" />
    </>
  ),
  bell: (
    <>
      <path d="M10.268 21a2 2 0 0 0 3.464 0" />
      <path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.5 18 8A6 6 0 0 0 6 8c0 4.5-1.411 5.956-2.738 7.326" />
    </>
  ),
  bar: (
    <>
      <path d="M3 3v18h18" />
      <rect x="7" y="10" width="3" height="7" />
      <rect x="12" y="6" width="3" height="11" />
      <rect x="17" y="13" width="3" height="4" />
    </>
  ),
  line: (
    <>
      <path d="M3 3v18h18" />
      <path d="m7 14 3-4 3 2 5-6" />
    </>
  ),
  'alert-triangle': (
    <>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <path d="M12 9v4M12 17h.01" />
    </>
  ),
  calendar: (
    <>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </>
  ),
  // Calendar with a marked day — "today". Same family as `calendar` (yesterday/
  // tomorrow) but distinct, and doesn't collide with `clock` (rota Hours).
  'calendar-today': (
    <>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
      <circle cx="12" cy="15.5" r="1.5" fill="currentColor" stroke="none" />
    </>
  ),
  paw: (
    <>
      <circle cx="11" cy="4" r="2" />
      <circle cx="18" cy="8" r="2" />
      <circle cx="20" cy="16" r="2" />
      <path d="M9 10a5 5 0 0 0-5 5c0 2 2 4 5 4s5-2 5-4a5 5 0 0 0-5-5z" />
    </>
  ),
  // THE THREE BUILDINGS — Freddi, 2026-09-11: "use kennel icon for big, and
  // shed for little", with `home` already carrying House.
  //
  // This is better than what it replaced, and the reason is worth keeping. The
  // first attempt drew one paw at two sizes, on the argument that Big and Little
  // are one thing at two sizes. They are not. They are two STRUCTURES in the
  // yard — the kennel block and the shed — and the desk already calls them that.
  // A glyph that names the building a dog sleeps in needs no legend; a paw
  // scaled 70% needs someone to explain it first.
  //
  // All three are buildings, so the ROOF is the only thing left to tell them
  // apart, and each one gets a different GEOMETRY of roof rather than a
  // different size of the same roof: the kennel arcs, the shed slants, the
  // house gables. Curve, diagonal, triangle — three shapes that stay separate
  // at the 14px the condensed pill draws them at.
  //
  // THE FIRST VERSION OF THIS DID NOT. The kennel was a pitched roof over a box
  // and so was `home`, separated only by being squatter — Freddi, 2026-09-11:
  // "all icons need to be distinct and big and house are too similar". He was
  // right, and so was the instinct behind it: height is a comparison, and a
  // comparison needs both glyphs side by side to read. Shape does not.
  //
  // A dog kennel — arched shell, arched mouth. Nothing else in the set curves.
  kennel: (
    <>
      <path d="M4 20v-7a8 8 0 0 1 16 0v7" />
      <path d="M2.5 20h19" />
      <path d="M9.5 20v-4a2.5 2.5 0 0 1 5 0v4" />
    </>
  ),
  // A garden shed — the single sloped pitch is the whole tell. A second gable
  // here and it becomes a small house, which is the one thing it must not be:
  // `home` is already in this menu, one row below.
  shed: (
    <>
      <path d="M2.5 11.4 20.5 6.2" />
      <path d="M5 10.9V20M18 8V20" />
      <path d="M3.5 20h16" />
      <path d="M9 20v-6h4.5v6" />
    </>
  ),
  // Water droplet — the "please wash" hygiene prompt on a leaving dog (staff
  // Today/Tomorrow briefing). A calm mint tag, never an alarm colour.
  droplet: <path d="M12 3s5 5.5 5 9a5 5 0 0 1-10 0c0-3.5 5-9 5-9z" />,
  // Food bowl — the food-at-house mark (staff Today briefing + booking flag).
  bowl: (
    <>
      <path d="M4 15h16M6 15a6 6 0 0 1 12 0M12 15v-3" />
      <path d="M3 19h18" />
    </>
  ),
  trash: (
    <>
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <path d="M10 11v6M14 11v6" />
    </>
  ),
  edit: (
    <>
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </>
  ),
  home: (
    <>
      <path d="M3 9.5 12 3l9 6.5" />
      <path d="M5 9.5V20a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5" />
      <path d="M9 21v-6h6v6" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  // A closed padlock — "this value is not yours to change", not "this is
  // secret". Its one caller is a registered contact's email in the household
  // modal, where the address IS the sign-in identity.
  lock: (
    <>
      <rect x="4" y="10.5" width="16" height="10" rx="2.5" />
      <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
    </>
  ),
  palm: (
    <>
      <path d="M12 22v-11" />
      <path d="M12 11C9 7.5 5.5 7.5 3.5 9.5" />
      <path d="M12 11c3-3.5 6.5-3.5 8.5-1.5" />
      <path d="M12 11c0-4-2-6.5-4.5-7.5" />
      <path d="M12 11c0-4 2-6.5 4.5-7.5" />
    </>
  ),
  // THE SELF-SERVICE CARDS' OWN MARKS, verbatim from the customer-booking-portal prototype (flows 4.7,
  // 5.1; Freddi, 2026-10-13: "add correct icons … as per protoyep"). Upload: a row's spine and the drop
  // zones. Image: the frame for nothing uploaded. Euro and syringe: the two cards' round subject marks.
  upload: (
    <>
      <path d="M12 16V4M6 10l6-6 6 6" />
      <path d="M4 20h16" />
    </>
  ),
  image: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="9" cy="10" r="2" />
      <path d="m21 17-5-5-7 7" />
    </>
  ),
  euro: <path d="M4 10h12M4 14h9M19 6a7.7 7.7 0 0 0-5.2-2A7.9 7.9 0 0 0 6 12c0 4.4 3.5 8 7.8 8 2 0 3.8-.8 5.2-2" />,
  syringe: (
    <>
      <path d="m18 2 4 4" />
      <path d="m17 7 3-3" />
      <path d="M19 9 8.7 19.3a2.4 2.4 0 0 1-3.4 0l-.6-.6a2.4 2.4 0 0 1 0-3.4L15 5" />
      <path d="m9 11 4 4" />
      <path d="m5 19-3 3" />
      <path d="m14 4 6 6" />
    </>
  ),
  wallet: (
    <>
      <rect x="3" y="6" width="18" height="13" rx="2" />
      <path d="M3 10h18" />
      <circle cx="16" cy="14" r="1.4" />
    </>
  ),
  message: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  // ----- Booking-flag glyphs (booking-flags story; lifted verbatim from the
  // validated prototype docs/design/booking-flags/prototype.html) -----
  // First booking — a new household: person + plus.
  'first-booking': (
    <>
      <circle cx="10" cy="7.5" r="3.3" />
      <path d="M4 20c0-3.6 2.7-6.3 6-6.3 1.3 0 2.5.4 3.5 1.1" />
      <path d="M19 6.5v5M16.5 9h5" />
    </>
  ),
  // Medication — a first-aid kit (ref: medicine icon).
  medication: (
    <>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7" />
      <path d="M12 11v5M9.5 13.5h5" />
    </>
  ),
  // Note to read — a written sheet with text lines.
  note: (
    <>
      <path d="M13 3H6.5A1.5 1.5 0 0 0 5 4.5v15A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5V9z" />
      <path d="M13 3v6h6" />
      <path d="M8.5 13h7M8.5 16.5h7" />
    </>
  ),
  // Vaccines due — a syringe, needle top-right (ref: syringe icon).
  vaccine: (
    <>
      <path d="M20.5 3.5l-3.2 3.2" />
      <path d="M16.2 5.6l2.2 2.2" />
      <rect x="7.7" y="9.2" width="9.2" height="5.4" rx="1.2" transform="rotate(45 12.3 11.9)" />
      <path d="M12.4 8.9l2 2" />
      <path d="M8 15.2l-1.7 1.7" />
      <path d="M5 15l3.4 3.4" />
    </>
  ),
  // NO vaccination record at all — the syringe above, struck through. Built from
  // the same paths rather than a borrowed glyph so the three vaccine states read
  // as one family. The plunger stroke is dropped: at badge size it and the
  // strike were fighting for the same few pixels.
  'vaccine-none': (
    <>
      <path d="M20.5 3.5l-3.2 3.2" />
      <path d="M16.2 5.6l2.2 2.2" />
      <rect x="7.7" y="9.2" width="9.2" height="5.4" rx="1.2" transform="rotate(45 12.3 11.9)" />
      <path d="M8 15.2l-1.7 1.7" />
      <path d="M5 15l3.4 3.4" />
      <path d="M3.5 20.5 20.5 3.5" />
    </>
  ),
  // A vaccination record that exists but cannot be read — the syringe with a
  // question mark. Distinct from 'vaccine-none' because they are different jobs:
  // one is a telephone call to the vet, the other is retyping a card.
  'vaccine-unknown': (
    <>
      <path d="M20.5 3.5l-3.2 3.2" />
      <path d="M16.2 5.6l2.2 2.2" />
      <rect x="7.7" y="9.2" width="9.2" height="5.4" rx="1.2" transform="rotate(45 12.3 11.9)" />
      <path d="M8 15.2l-1.7 1.7" />
      <path d="M5 15l3.4 3.4" />
      <path d="M17.6 15.2a2 2 0 1 1 2.4 1.9v1" />
      <path d="M20 21h.01" />
    </>
  ),
  // Keep apart — two dogs squaring up (ref: do not mix icon, mirrored + clash).
  'do-not-mix': (
    <>
      <g>
        <path d="M4.5 10.5L6.2 4.5 9.2 9.8" />
        <path d="M2.8 13.5C2.6 11 4.2 9.8 6.2 9.8 8.4 9.8 9.8 11.2 11 12.4" />
        <path d="M2.8 13.5C3.6 16 6.4 17 9 15.6L11 14" />
        <path d="M6 12.6h.01" />
      </g>
      <g transform="translate(24 0) scale(-1 1)">
        <path d="M4.5 10.5L6.2 4.5 9.2 9.8" />
        <path d="M2.8 13.5C2.6 11 4.2 9.8 6.2 9.8 8.4 9.8 9.8 11.2 11 12.4" />
        <path d="M2.8 13.5C3.6 16 6.4 17 9 15.6L11 14" />
        <path d="M6 12.6h.01" />
      </g>
      <path d="M12 9.5l-1 2 2 .6-1.4 2.4" />
    </>
  ),
  // Return arrow — the carried-forward-note indicator.
  undo: (
    <>
      <path d="M9 14L4 9l5-5" />
      <path d="M4 9h11a5 5 0 0 1 5 5v3" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>
  ),
  // A household — a group of people. Distinct from single `user` (an account)
  // and from `clock` (a helper). Used by the search owner rows.
  users: (
    <>
      <circle cx="9" cy="8" r="3.4" />
      <path d="M3.5 20a5.5 5.5 0 0 1 11 0" />
      <path d="M15.5 4.9a3.4 3.4 0 0 1 0 6.2" />
      <path d="M18.5 20a5.6 5.6 0 0 0-3.2-5.05" />
    </>
  ),
};

export interface IconProps {
  name: string;
  size?: number;
  className?: string;
}

export function Icon({ name, size = 18, className }: IconProps) {
  return (
    <svg
      className={'icon' + (className ? ' ' + className : '')}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {ICONS[name] ?? null}
    </svg>
  );
}
